// Rammen om hele appen: vælger side ud fra den interne sti og kræver login, hvor det er nødvendigt.
import { Suspense, useEffect, useState } from 'react';
import type { Bruger } from './api/api';
import { somDemo } from './api/somDemo';
import { brugerKontorSkal, erKontor } from '../core/roller';
import { Bekraeft, EksportVaert, Henter, Toast, erIndlejret, gaaTil, useApi, useData, useRute, useSprogPaaSiden } from './lib';
import { anvendIndlejretFarve, foraeldreGodkendt } from './indlejret';
import { bookingUdenRamme } from '../core/indlejring';
import { anvendKlientTema } from './klientTema';
import { brandNu, saetBrandVisning } from './brandValg';
import { KONSOL_VAERT, konsolAdresse, paaKonsol } from './pixieVaert';
import { StagingBjaelke } from './Staging';
import { senereSider } from './senere';
import { FIL } from './sideFiler';

// Hver side hentes først, når den vises (se senere.ts og sideFiler.ts)
const { PixieKonsol } = senereSider(FIL.pixie, 'PixieKonsol');
const { PixieBekraeft, PixieForside, PixieJura, PixieOpret } = senereSider(FIL.pixieForside, 'PixieBekraeft', 'PixieForside', 'PixieJura', 'PixieOpret');
const { BetalingTest } = senereSider(FIL.abonnement, 'BetalingTest');
const { HjemmesideTest } = senereSider(FIL.hjemmeside, 'HjemmesideTest');
const { IndlejringVis } = senereSider(FIL.indlejring, 'IndlejringVis');
const { EkstraGodkend } = senereSider(FIL.ekstra, 'EkstraGodkend');
const { Start } = senereSider(FIL.start, 'Start');
const { Login, Glemt, Nulstil, Invitation } = senereSider(FIL.login, 'Login', 'Glemt', 'Nulstil', 'Invitation');
const { Booking, Bekraeftelse } = senereSider(FIL.booking, 'Booking', 'Bekraeftelse');
const { PartnerSide } = senereSider(FIL.partner, 'PartnerSide');
const { Mine } = senereSider(FIL.mine, 'Mine');
const { LoginLink } = senereSider(FIL.loginLink, 'LoginLink');
const { Afmeld } = senereSider(FIL.afmeld, 'Afmeld');
const { NyhedAfmeld } = senereSider(FIL.nyhedAfmeld, 'NyhedAfmeld');
const { Anmeld } = senereSider(FIL.anmeld, 'Anmeld');
const { BetalingRetur } = senereSider(FIL.betalingRetur, 'BetalingRetur');
const { Kundecenter } = senereSider(FIL.kundecenter, 'Kundecenter');
const { ChatWidget } = senereSider(FIL.chatWidget, 'ChatWidget');
const { GodkendArbejde } = senereSider(FIL.godkend, 'GodkendArbejde');
const { NyTid } = senereSider(FIL.nyTid, 'NyTid');
const { UdlaegUpload } = senereSider(FIL.udlaeg, 'UdlaegUpload');
const { AdminSkal } = senereSider(FIL.admin, 'AdminSkal');
const { SteamerSkal } = senereSider(FIL.steamer, 'SteamerSkal');

export function hjemFor(b: Bruger | null): string {
  if (!b) return '/login';
  if (b.rolle === 'saelger') return '/admin/crm';
  if (erKontor(b.rolle)) return '/admin/overblik';
  if (b.rolle === 'steamer') return '/steamer/dag';
  return '/mine';
}

export function useBruger(): Bruger | null {
  const api = useApi();
  const [b, setB] = useState(api.bruger());
  useEffect(() => api.lyt(() => setB((gl) => { const ny = api.bruger(); return JSON.stringify(gl) === JSON.stringify(ny) ? gl : ny; })), [api]);
  return b;
}

function ForkertRolle({ bruger }: { bruger: Bruger }) {
  return (
    <div className="login-form" style={{ minHeight: '100vh' }}>
      <div className="login-boks kort">
        <h2>Du har ikke adgang til denne side</h2>
        <p className="svag">Du er logget ind som {bruger.navn}. Siden hører til en anden type bruger.</p>
        <div className="raekke"><button className="knap primaer" onClick={() => gaaTil(hjemFor(bruger))}>Gå til min forside</button></div>
      </div>
    </div>
  );
}

/** Kundens side (Mine bookinger), mens man er logget ind som medarbejder på samme adresse: én knap logger ud, og så kan
 *  kunden logge ind (login-linket fra kundens mail skifter selv til kundens login). */
function SomMedarbejder() {
  const api = useApi();
  const [arbejder, setArbejder] = useState(false);
  return (
    <div className="login-form" style={{ minHeight: '100vh' }}>
      <div className="login-boks kort stak" data-som-medarbejder>
        <h1>Du er logget ind som medarbejder</h1>
        <p className="svag">Log ud og fortsæt som kunde for at se kundens bookinger.</p>
        <button className="knap primaer" disabled={arbejder} onClick={async () => { setArbejder(true); try { await api.logUd(); } finally { setArbejder(false); } }}>Log ud og fortsæt som kunde</button>
      </div>
    </div>
  );
}

export function App() {
  const rute = useRute();
  const bruger = useBruger();
  useSprogPaaSiden();
  const api = useApi();
  const [sti, qs] = rute.split('?');
  const q = new URLSearchParams(qs ?? '');
  const kundeside = ['book', 'p', 'bekraeftelse', 'kundecenter', 'chat', 'mine', 'link', 'ekstra', 'kvittering', 'godkend', 'medlem', 'hjemmeside', 'ny-tid', 'af', 'nyhedsbrev', 'anmeld', 'betaling', 'indlejring'].includes(sti.split('/')[1] ?? '');
  const platform = sti.split('/')[1] === 'pixie';
  const kl = useData(() => api.klient());
  const klient = kl.data;
  useEffect(() => { document.body.dataset.flade = kundeside ? 'kunde' : platform ? 'platform' : 'pixie'; }, [kundeside, platform]);
  // Ét fælles Freemi-design for alle. Klientens (eller subdomænets, fx SteamFox) logo og navn vises på siden.
  const brandId = kundeside ? brandNu() : null;
  const br = useData(() => (brandId ? api.katalog(brandId).then((k) => k.brand ?? null) : Promise.resolve(null)), [brandId]).data ?? null;
  useEffect(() => {
    anvendKlientTema(platform ? null : klient?.slug ?? null);
    saetBrandVisning(br ? { id: br.id, navn: br.navn, logo: br.design.logo } : null);
    if (erIndlejret()) anvendIndlejretFarve(br?.design ?? klient?.brand ?? null);
    document.title = platform ? 'Freemi' : br?.navn ?? klient?.navn ?? 'Freemi';
  }, [platform, klient, br]);
  const [s0, s1, s2] = sti.split('/').filter(Boolean);
  let side: React.ReactNode;
  const kraev = (ok: boolean, el: React.ReactNode) => (!bruger ? <Login naeste={rute} /> : ok ? el : <ForkertRolle bruger={bruger} />);
  switch (s0) {
    case 'pixie': side = s1 === 'konsol' ? (KONSOL_VAERT && location.hostname !== KONSOL_VAERT ? <TilKonsol /> : <PixieKonsol />) : s1 === 'opret' ? <PixieOpret /> : s1 === 'bekraeft' ? <PixieBekraeft token={s2 ?? ''} /> : s1 === 'vilkaar' || s1 === 'privatliv' || s1 === 'databehandler' ? <PixieJura id={s1} /> : s1 === 'betaling' ? kraev(bruger?.rolle === 'ejer', <BetalingTest />) : <PixieForside />; break;
    case 'book': side = <Booking key={'book' + (q.get('fra') ?? '') + (q.get('igen') ?? '')} postnr={q.get('postnr') ?? undefined} fraKode={q.get('fra') ?? undefined} igen={q.get('igen') ?? undefined} />; break;
    case 'hjemmeside': side = <HjemmesideTest />; break;
    case 'indlejring': side = <IndlejringVis brand={brandId} farve={q.get('farve') === 'brand'} />; break;
    case 'medlem': side = <Booking key="medlem" medlemFlow />; break;
    case 'p': side = <PartnerSide key={s1} slug={(s1 ?? '').toLowerCase()} />; break;
    case 'bekraeftelse': side = <Bekraeftelse kode={s1 ?? ''} partner={q.get('p') ?? undefined} />; break;
    case 'login': side = bruger ? <Omdiriger til={hjemFor(bruger)} /> : <Login email={q.get('email') ?? undefined} />; break;
    case 'glemt': side = <Glemt />; break;
    case 'nulstil': side = <Nulstil token={s1 ?? ''} />; break;
    case 'invitation': side = <Invitation token={s1 ?? ''} />; break;
    case 'kundecenter': side = <Kundecenter token={s1} />; break;
    case 'chat': side = <ChatWidget />; break;
    case 'ekstra': side = <EkstraGodkend token={s1 ?? ''} />; break;
    case 'godkend': side = <GodkendArbejde token={s1 ?? ''} />; break;
    case 'ny-tid': side = <NyTid token={s1 ?? ''} />; break;
    case 'udlaeg': side = <UdlaegUpload token={s1 ?? ''} />; break;
    case 'link': side = <LoginLink token={s1 ?? ''} />; break;
    case 'af': side = <Afmeld token={s1 ?? ''} />; break;
    case 'nyhedsbrev': side = <NyhedAfmeld token={s1 ?? ''} />; break;
    case 'betaling': side = <BetalingRetur ok={s1 === 'ok'} noegle={s2} />; break;
    case 'anmeld': side = <Anmeld key={s1} token={s1 ?? ''} stjerner={s2} />; break;
    case 'mine': side = bruger && bruger.rolle !== 'kunde' ? <SomMedarbejder /> : kraev(true, <Mine fane={s1 ?? 'bookinger'} />); break;
    case 'admin': side = kraev(brugerKontorSkal(bruger?.rolle), <AdminSkal side={s1 ?? 'overblik'} id={s2} />); break;
    case 'steamer': side = kraev(bruger?.rolle === 'steamer', <SteamerSkal side={s1 ?? 'dag'} id={s2} />); break;
    default: side = <Start />;
  }
  // Konsollens subdomæne har kun konsollen og siden til en ny adgangskode (fra "Glemt adgangskode?")
  if (paaKonsol() && s0 !== 'pixie' && s0 !== 'nulstil') side = <Omdiriger til="/pixie/konsol" />;
  // Adressen peger ikke på en virksomhed (kun i drift – testversionen har altid en klient)
  else if (kl.fejl && !platform) side = <UkendtAdresse fejl={kl.fejl} />;
  // Bookingen i en ramme på en hjemmeside, kontoret ikke har godkendt (i drift afviser edge-funktionen/CSP'en den også)
  else if (klient && erIndlejret() && foraeldreGodkendt(klient, brandNu()) === false) side = <IkkeGodkendt />;
  return <><Suspense fallback={<Henter />}>{side}</Suspense><Toast /><EksportVaert /><DemoBjaelke bruger={bruger} rute={rute} /><StagingBjaelke bruger={bruger} rute={rute} /></>;
}

function IkkeGodkendt() {
  return (
    <main className="ij-afvist" data-freemi-afvist>
      <h1>Bookingen kan ikke vises her</h1>
      <p>Siden er ikke godkendt til at vise bookingen. Du kan stadig booke – åbn bookingen i et nyt vindue.</p>
      <a className="knap primaer" href={bookingUdenRamme(new URL(location.href))} target="_blank" rel="noopener">Åbn bookingen</a>
    </main>
  );
}

function UkendtAdresse({ fejl }: { fejl: string }) {
  return (
    <div className="login-form" style={{ minHeight: '100vh' }}>
      <div className="login-boks kort stak">
        <h1>Siden findes ikke</h1>
        <p className="svag">{fejl}</p>
        <div className="raekke"><a className="knap primaer" href="/">Gå til Freemi</a></div>
      </div>
    </div>
  );
}

/** Konsollen ligger på sit eget subdomæne (fx admin.freemi.dk) – ikke på hjemmesiden. */
function TilKonsol() { useEffect(() => { location.replace(konsolAdresse()); }, []); return null; }

function Omdiriger({ til }: { til: string }) { useEffect(() => { gaaTil(til, true); }, [til]); return null; }

/** Kun i testversionen: skift perspektiv og nulstil data. */
function DemoBjaelke({ bruger, rute }: { bruger: Bruger | null; rute: string }) {
  const api = somDemo(useApi());
  const [nulstil, setNulstil] = useState(false);
  if (!api) return null;
  const brugere = api.demoBrugere();
  const klienter = api.klientListe();
  const vaerdi = rute.startsWith('/pixie/konsol') ? 'pixie' : rute.startsWith('/pixie') ? 'pixieside' : bruger ? brugere.find((d) => d.email === bruger.email)?.email ?? 'anden' : 'ingen';
  const harBund = rute.startsWith('/admin') || rute.startsWith('/steamer') || rute.startsWith('/mine');
  return (
    <div className={'demo-bjaelke' + (harBund ? ' over-bund' : '') + (/\/chat\/./.test(rute) ? ' oppe' : '') + (rute.startsWith('/book') ? ' over-pris' : '')} aria-label="Testversion">
      <span>Test</span>
      {klienter.length > 1 && <select aria-label="Skift klient" value={api.klientSlug()} onChange={(e) => { api.skiftKlient(e.target.value); gaaTil('/login'); }}>{klienter.map((k) => <option key={k.slug} value={k.slug}>{k.navn}</option>)}</select>}
      <select aria-label="Skift perspektiv" value={vaerdi} onChange={async (e) => {
        const v = e.target.value;
        if (v === 'ingen') { await api.logUd(); gaaTil('/start'); return; }
        if (v === 'pixie') { api.demoPlatformLogin(); gaaTil('/pixie/konsol'); return; }
        if (v === 'pixieside') { gaaTil('/pixie'); return; }
        if (v === 'book') { gaaTil('/book'); return; }
        const b = api.demoLogin(v); gaaTil(hjemFor(b));
      }}>
        <option value="ingen">Ikke logget ind</option>
        {brugere.map((d) => <option key={d.email} value={d.email}>{d.tekst}</option>)}
        {vaerdi === 'anden' && <option value="anden">{bruger?.navn}</option>}
        <option value="book">Bookingsiden</option>
        <option value="pixieside">Freemis forside (opret din Freemi)</option>
        <option value="pixie">Freemi-konsol (platform)</option>
      </select>
      <button onClick={() => setNulstil(true)}>Nulstil</button>
      {nulstil && <Bekraeft titel="Nulstil testdata?" tekst="Alle ændringer i testversionen slettes, og eksempeldata lægges ind igen. Det påvirker kun denne browser." knap="Nulstil" fare luk={() => setNulstil(false)} ok={() => { api.nulstil(); gaaTil(hjemFor(api.bruger())); }} />}
    </div>
  );
}
